import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can, canViewHR, canEditSalary } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, MetricCard, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field,
  Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import {
  Plus, DownloadSimple, MagnifyingGlass, PencilSimple, Trash, UserCircle, Eye, Confetti, IdentificationCard,
} from "@phosphor-icons/react";
import PhotoUploader, { PhotoThumb } from "@/components/PhotoUploader";
import SmartInput from "@/components/SmartInput";
import MicDictate from "@/components/MicDictate";

const BLANK = {
  employee_code: "", damstra_id: "", full_name: "", nrc_number: "", tpin: "", social_security_number: "",
  gender: "", date_of_birth: "",
  position: "", department: "Operations", employment_type: "full_time",
  hire_date: new Date().toISOString().slice(0, 10),
  termination_date: "", status: "active", salary_zmw: 0, roster_id: "",
  exempt_from_napsa: false,
  phone: "", email: "", address: "",
  account_number: "", bank_name: "",
  next_of_kin: "", next_of_kin_phone: "",
  project_code: "", notes: "",
};

const STATUS_COLOR = { active: "#00C853", on_leave: "#FFC000", suspended: "#FF8C00", terminated: "#FF2A2A" };
const PIE_COLORS = ["#002FA7", "#00C853", "#FFC000", "#FF2A2A", "#52525b", "#a1a1aa"];

export default function HR() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rosters, setRosters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [viewing, setViewing] = useState(null);
  const canEdit = canViewHR(user.role);
  const canDelete = user.role === "admin";
  const canSetSalary = canEditSalary(user.role);

  async function load() {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("status", statusFilter);
      const [list, sum] = await Promise.all([
        api.get(`/employees?${params}`),
        api.get("/hr/summary"),
      ]);
      setRows(list.data); setSummary(sum.data);
    } catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, [q, statusFilter]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/rosters"); setRosters(data || []); }
      catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
    (async () => {
      try { const { data } = await api.get("/lookups/department"); setDepartments(data || []); }
      catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setOpen(true); }
  function openEdit(e) {
    setEditing(e);
    setForm({
      ...BLANK, ...e,
      date_of_birth: e.date_of_birth ? String(e.date_of_birth).slice(0, 10) : "",
      hire_date: e.hire_date ? String(e.hire_date).slice(0, 10) : "",
      termination_date: e.termination_date ? String(e.termination_date).slice(0, 10) : "",
      project_code: e.project_code || "",
    });
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    try {
      const toIso = (d) => d ? new Date(d).toISOString() : null;
      const payload = {
        ...form,
        salary_zmw: Number(form.salary_zmw || 0),
        hire_date: toIso(form.hire_date),
        date_of_birth: toIso(form.date_of_birth),
        termination_date: toIso(form.termination_date),
        gender: form.gender || null,
        project_code: form.project_code || null,
      };
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(emp) {
    if (!window.confirm(`Delete employee record for ${emp.full_name}?`)) return;
    try { await api.delete(`/employees/${emp.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="hr-page">
      <PageHeader
        overline="Module / HR"
        title="People"
        subtitle="Employee directory, headcount and monthly payroll at a glance."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `employees-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="emp-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Add Employee
              </BtnPrimary>
            )}
          </>
        }
      />

      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <MetricCard testId="hr-active" label="Active" value={summary.total_active} accent="success"
              delta={`${summary.total} total`} deltaLabel="Headcount" />
            <MetricCard testId="hr-onleave" label="On Leave" value={summary.total_on_leave}
              accent="warning" delta={`${summary.total_terminated} terminated`} deltaLabel="Other" />
            <MetricCard testId="hr-payroll" label="Monthly Payroll" value={fmtZMW(summary.monthly_payroll_zmw)}
              accent="primary" delta="Active employees" deltaLabel="Basis" />
            <MetricCard testId="hr-departments" label="Departments" value={summary.by_department.length}
              accent="default" delta={summary.by_type.find((t) => t.name === "full_time")?.value || 0}
              deltaLabel="Full-time" />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            <div className="phizo-card p-5">
              <div className="phizo-label mb-2">Headcount by Department</div>
              <div className="font-heading text-xl font-bold mb-3">Distribution</div>
              {summary.by_department.length === 0 ? (
                <div className="h-52 flex items-center justify-center text-sm text-[#a1a1aa]">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={summary.by_department}>
                    <CartesianGrid stroke="#e4e4e7" vertical={false} />
                    <XAxis dataKey="name" stroke="#a1a1aa" fontSize={11} />
                    <YAxis stroke="#a1a1aa" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }} />
                    <Bar dataKey="value" fill="#002FA7" radius={0} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="phizo-card p-5">
              <div className="phizo-label mb-2">By Employment Type</div>
              <div className="font-heading text-xl font-bold mb-3">Mix</div>
              {summary.by_type.length === 0 ? (
                <div className="h-52 flex items-center justify-center text-sm text-[#a1a1aa]">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie data={summary.by_type} dataKey="value" nameKey="name"
                      innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {summary.by_type.map((entry, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {summary.upcoming_birthdays.length + summary.new_hires_30d.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
              {summary.upcoming_birthdays.length > 0 && (
                <div className="phizo-card p-5">
                  <div className="phizo-label mb-1 flex items-center gap-2">
                    <Confetti size={14} weight="bold" /> Birthdays (next 30 days)
                  </div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {summary.upcoming_birthdays.map((b) => (
                      <li key={`${b.name}-${b.date}`} className="flex justify-between border-b border-[#e4e4e7] py-1">
                        <span>{b.name}</span>
                        <span className="mono text-xs text-[#52525b]">{fmtDate(b.date)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {summary.new_hires_30d.length > 0 && (
                <div className="phizo-card p-5">
                  <div className="phizo-label mb-1 flex items-center gap-2">
                    <IdentificationCard size={14} weight="bold" /> New hires (last 30 days)
                  </div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {summary.new_hires_30d.map((h) => (
                      <li key={`${h.name}-${h.date}`} className="flex justify-between border-b border-[#e4e4e7] py-1">
                        <span>{h.name} <span className="text-[#a1a1aa] text-xs">— {h.position}</span></span>
                        <span className="mono text-xs text-[#52525b]">{fmtDate(h.date)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Search & filter */}
      <div className="phizo-card p-3 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-[#a1a1aa]" />
          <input
            data-testid="hr-search"
            placeholder="Search name, code, position, phone…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full border border-[#e4e4e7] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
          />
        </div>
        <select
          data-testid="hr-status-filter"
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#e4e4e7] bg-white px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="on_leave">On Leave</option>
          <option value="suspended">Suspended</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No employees yet." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th className="w-12"></th>
                  <th>Code</th><th>Name</th><th>Position</th><th>Department</th>
                  <th>Type</th><th>Hire Date</th>
                  <th className="text-right">Salary (ZMW)</th>
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((emp) => (
                  <tr
                    key={emp.id}
                    data-testid={`emp-row-${emp.employee_code}`}
                    onClick={() => nav(`/hr/${emp.id}`)}
                    className="cursor-pointer hover:bg-[#f4f4f5]"
                  >
                    <td className="py-2">
                      <PhotoThumb value={emp.photo_url} size={36} testId={`emp-thumb-${emp.employee_code}`} />
                    </td>
                    <td className="mono font-semibold">
                      {emp.damstra_id || emp.employee_code}
                      {emp.damstra_id && emp.damstra_id !== emp.employee_code && (
                        <div className="mono text-[10px] text-[#a1a1aa] font-normal">
                          Sys: {emp.employee_code}
                        </div>
                      )}
                    </td>
                    <td className="font-medium">{emp.full_name}</td>
                    <td className="text-sm">{emp.position}</td>
                    <td className="text-sm text-[#52525b]">{emp.department}</td>
                    <td className="text-xs uppercase tracking-wider">{emp.employment_type.replace("_", " ")}</td>
                    <td className="text-sm">{fmtDate(emp.hire_date)}</td>
                    <td className="text-right mono">{fmtZMW(emp.salary_zmw)}</td>
                    <td>
                      <span className="phizo-status"
                        style={{ background: STATUS_COLOR[emp.status] || "#a1a1aa",
                                 color: emp.status === "active" || emp.status === "on_leave" ? "#09090b" : "#fff",
                                 borderColor: STATUS_COLOR[emp.status] || "#a1a1aa" }}>
                        {emp.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <BtnGhost onClick={() => setViewing(emp)} data-testid={`emp-view-${emp.employee_code}`}>
                        <Eye size={12} weight="bold" />
                      </BtnGhost>
                      {canEdit && (
                        <BtnGhost onClick={() => openEdit(emp)}>
                          <PencilSimple size={12} weight="bold" />
                        </BtnGhost>
                      )}
                      {canDelete && (
                        <BtnGhost onClick={() => remove(emp)} className="text-[#FF2A2A]">
                          <Trash size={12} weight="bold" />
                        </BtnGhost>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit */}
      <Modal open={open} onClose={() => setOpen(false)}
        title={editing ? `Edit ${editing.full_name}` : "New Employee"} wide testId="hr-form-modal">
        <form onSubmit={save} className="space-y-4">
          {/* Photo uploader */}
          <div className="border border-[#e4e4e7] p-4 bg-[#fafafa]">
            <div className="phizo-label mb-2">Employee Photo</div>
            <PhotoUploader
              value={form.photo_url}
              onChange={(url) => setForm({ ...form, photo_url: url })}
              category="employee"
              size={96}
              testId="emp-photo"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee Code">
              <Input required disabled={!!editing} data-testid="emp-code"
                value={form.employee_code}
                onChange={(e) => setForm({ ...form, employee_code: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Damstra ID (Mine Ref No.)" hint="Shown on the payslip as Mine Reference No.">
              <Input value={form.damstra_id} data-testid="emp-damstra"
                onChange={(e) => setForm({ ...form, damstra_id: e.target.value.toUpperCase() })}
                placeholder="e.g. C37405" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="active">Active</option>
                <option value="on_leave">On Leave</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Full Name">
                <Input required data-testid="emp-name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </Field>
            </div>
            <Field label="Position / Job Title">
              <Input required data-testid="emp-position"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })} />
            </Field>
            <Field label="Department" hint="Pick from master list or type a new one">
              <SmartInput
                testId="emp-department"
                value={form.department}
                onChange={(v) => setForm({ ...form, department: v })}
                options={departments.map((d) => ({ value: d.name, hint: d.code || "" }))}
                placeholder="e.g. Operations"
              />
            </Field>
            <Field label="Employment Type">
              <Select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="casual">Casual</option>
              </Select>
            </Field>
            <Field label="Gender">
              <Select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </Select>
            </Field>
            <Field label="NRC Number">
              <Input value={form.nrc_number}
                onChange={(e) => setForm({ ...form, nrc_number: e.target.value })} />
            </Field>
            <Field label="TPIN (ZRA)">
              <Input value={form.tpin} data-testid="emp-tpin"
                onChange={(e) => setForm({ ...form, tpin: e.target.value })}
                placeholder="10-digit tax number" />
            </Field>
            <Field label="Social Security Number">
              <Input value={form.social_security_number} data-testid="emp-ssn"
                onChange={(e) => setForm({ ...form, social_security_number: e.target.value })}
                placeholder="NAPSA / SSN" />
            </Field>
            <Field label="Work Roster">
              <Select value={form.roster_id || ""} data-testid="emp-roster"
                onChange={(e) => setForm({ ...form, roster_id: e.target.value })}>
                <option value="">— unassigned —</option>
                {rosters.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Date of Birth">
              <Input type="date" value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
            </Field>
            <Field label="Hire Date">
              <Input type="date" required value={form.hire_date}
                onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
            </Field>
            <Field label="Termination Date">
              <Input type="date" value={form.termination_date}
                onChange={(e) => setForm({ ...form, termination_date: e.target.value })} />
            </Field>
            <Field label={`Monthly Salary — ZMW${canSetSalary ? "" : " — Admin / HR / Finance only"}`}>
              <Input type="number" step="0.01" value={canSetSalary ? form.salary_zmw : ""}
                data-testid="emp-salary"
                disabled={!canSetSalary}
                onChange={(e) => setForm({ ...form, salary_zmw: e.target.value })}
                placeholder={canSetSalary ? "" : "Restricted"} />
            </Field>
            <Field label="Retirement age (exempt from NAPSA)" hint="Tick if the employee is past retirement age and no longer contributes to NAPSA">
              <label className="flex items-center gap-2 text-sm mt-1">
                <input
                  type="checkbox"
                  data-testid="emp-exempt-napsa"
                  checked={!!form.exempt_from_napsa}
                  onChange={(e) => setForm({ ...form, exempt_from_napsa: e.target.checked })}
                />
                <span>Exempt from NAPSA on payroll</span>
              </label>
            </Field>
            <Field label="Bank Name" hint="Shown on payslip footer">
              <Input value={form.bank_name || ""}
                data-testid="emp-bank-name"
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                placeholder="e.g. Stanbic Bank, ZANACO" />
            </Field>
            <Field label="Bank Account Number" hint="Populates the 'Bank Account No' field on payslips">
              <Input value={form.account_number || ""}
                data-testid="emp-account-number"
                onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                placeholder="e.g. 9130001234567" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Next of Kin">
              <Input value={form.next_of_kin}
                onChange={(e) => setForm({ ...form, next_of_kin: e.target.value })} />
            </Field>
            <Field label="Next of Kin Phone">
              <Input value={form.next_of_kin_phone}
                onChange={(e) => setForm({ ...form, next_of_kin_phone: e.target.value })} />
            </Field>
            <div className="col-span-2">
              <Field label="Address">
                <Textarea value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <label className="phizo-label">Notes</label>
                <MicDictate
                  context="HR employee notes; payroll"
                  onTranscript={(text) =>
                    setForm((f) => ({ ...f, notes: (f.notes ? f.notes + " " : "") + text }))
                  }
                  testId="hr-notes-mic"
                />
              </div>
              <Textarea value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="hr-notes" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="emp-save">{editing ? "Save" : "Add Employee"}</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* View */}
      <Modal open={!!viewing} onClose={() => setViewing(null)}
        title={viewing ? viewing.full_name : ""} wide>
        {viewing && (
          <div className="space-y-4">
            {viewing.photo_url && (
              <div className="flex justify-center pb-3 border-b border-[#e4e4e7]">
                <PhotoThumb value={viewing.photo_url} size={140} testId="emp-view-photo" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="phizo-label">Employee Code</div><div className="mono font-bold">{viewing.employee_code}</div></div>
            <div><div className="phizo-label">Damstra ID / Mine Ref</div><div className="mono font-bold">{viewing.damstra_id || "—"}</div></div>
            <div><div className="phizo-label">Status</div>
              <span className="phizo-status"
                style={{ background: STATUS_COLOR[viewing.status] || "#a1a1aa",
                         color: "#09090b", borderColor: STATUS_COLOR[viewing.status] || "#a1a1aa" }}>
                {viewing.status.replace("_", " ")}
              </span>
            </div>
            <div><div className="phizo-label">Position</div><div>{viewing.position}</div></div>
            <div><div className="phizo-label">Department</div><div>{viewing.department}</div></div>
            <div><div className="phizo-label">Type</div><div>{viewing.employment_type.replace("_", " ")}</div></div>
            <div><div className="phizo-label">Gender</div><div>{viewing.gender || "—"}</div></div>
            <div><div className="phizo-label">DOB</div><div>{fmtDate(viewing.date_of_birth)}</div></div>
            <div><div className="phizo-label">NRC</div><div className="mono">{viewing.nrc_number || "—"}</div></div>
            <div><div className="phizo-label">TPIN</div><div className="mono">{viewing.tpin || "—"}</div></div>
            <div><div className="phizo-label">Social Security No.</div><div className="mono">{viewing.social_security_number || "—"}</div></div>
            <div><div className="phizo-label">Hire Date</div><div>{fmtDate(viewing.hire_date)}</div></div>
            <div><div className="phizo-label">Termination</div><div>{fmtDate(viewing.termination_date)}</div></div>
            <div><div className="phizo-label">Phone</div><div className="mono">{viewing.phone || "—"}</div></div>
            <div><div className="phizo-label">Email</div><div className="mono text-xs">{viewing.email || "—"}</div></div>
            <div><div className="phizo-label">Next of Kin</div><div>{viewing.next_of_kin || "—"}</div></div>
            <div><div className="phizo-label">Next of Kin Phone</div><div className="mono">{viewing.next_of_kin_phone || "—"}</div></div>
            <div><div className="phizo-label">Monthly Salary</div><div className="font-heading text-xl font-black">{fmtZMW(viewing.salary_zmw)}</div></div>
            <div><div className="phizo-label">Project</div><div className="mono">{viewing.project_code || "—"}</div></div>
            {viewing.address && (
              <div className="col-span-2">
                <div className="phizo-label">Address</div>
                <div className="whitespace-pre-line text-sm">{viewing.address}</div>
              </div>
            )}
            {viewing.notes && (
              <div className="col-span-2">
                <div className="phizo-label">Notes</div>
                <div className="whitespace-pre-line text-sm">{viewing.notes}</div>
              </div>
            )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
